<script setup>
import router from '@/router';
import { defineAsyncComponent, computed, useSlots } from 'vue';

const slots = useSlots();
const props = defineProps({
  type: {
    type: String,
    default: 'transparent',
    validator(value) {
      return ['transparent', 'white', 'colored', 'dark'].includes(value)
    }
  },
  low: {
    type: Boolean,
    default: false,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  iconSize: {
    type: String,
    default: null,
  },
  wide: {
    type: Boolean,
    default: false,
  },
  badge: {
    type: Number,
    default: 0,
  },
  border: {
    type: Boolean,
    default: false,
  },
  to: {
    type: Object,
    default: null,
  },
  href: {
    type: String,
    default: null,
  }
});

const iconComponent = computed(() => {
  if (props.icon) {
    return defineAsyncComponent(() => import(`@/components/icons/Icon${props.icon}.vue`));
  }
  return null;
});

const slotFilled = computed(() => {
  return slots.default != undefined;
});

const click = () => {
  if (props.to) {
    router.push(props.to);
  }

  if (props.href) {
    document.location = props.href;
    //window.open(props.href, '_blank');
  }
};

</script>

<template>
  <button class="button" :class="[ `button--${type}`, { 'button--wide': wide, 'button--low': low, 'button--border': border, 'button--icon-only': iconOnly, 'button--with-text': slotFilled } ]" @mouseup.prevent="click">
    <div class="button__content">
      <span v-if="icon" class="button__icon" :class="{'button__icon--fixed-size' : iconSize}" :style="{ width: iconSize, height: iconSize }">
        <component :is="iconComponent" />
      </span>
      <span v-if="!iconOnly && slotFilled" class="button__text"><slot /></span>
    </div>
    <span v-if="badge" class="button__badge">{{ badge }}</span>
  </button>
</template>

<style lang="scss" scoped>
.button {
  --colored-background: linear-gradient(137deg, #FF6D33 8.62%, #FBAC3F 93.36%), #FBAC3F;
  --dark-background: #94d4fa;
  --dark-background-old: linear-gradient(0deg, #2D2D3A 0%, #2D2D3A 100%), #2D2D3A;

  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;

  min-width: 56px;

  width: 56px;
  height: 56px;
  padding: 0 16px;
  flex-shrink: 0;

  background: transparent;
  border-radius: 28px;

  transition: transform 0.1s linear;

  &:disabled {
    pointer-events: none;
    color: rgba(45, 45, 58, 0.30);
    background-color: #B2B2BC;
  }

  &:active {
    transform: translateY(3px);

    .button__badge {
      // Keep the badge in place
      transform: translate(calc(-50% + 14px), calc(-50% - 15px));
    }
  }

  &--with-text {
    width: fit-content;
  }

  &--icon-only {
    padding: 0;
    width: fit-content;
    height: unset;
    min-width: unset;

    &:disabled {
      background-color: transparent;
    }

    // .button__badge {
    //   right: 10px;
    //   top: -10px;
    // }
  }

  &--wide {
    min-width: 90px;
  }

  &--low {
    height: 48px;
    border-radius: 24px;
  }

  &--white {
    background: var(--clr-white);
    color: rgba(45, 45, 58, 1);


    @media (hover: hover) {
      &:hover {
        color: var(--clr-white);
        background: rgba(45, 45, 58, 1);
      }
    }

    &:active {
      background: var(--colored-background);
    }
  }

  &--dark {
    color: white;
    background: var(--dark-background);

    @media (hover: hover) {
      &:hover {
        background: var(--colored-background);
        color: white;
      }
    }

    &:disabled {
      color: rgba(45, 45, 58, 0.30);
      background: #B2B2BC;
    }

    &:active {
      color: white;
      background: var(--dark-background);
    }

    @media (hover: none) {
      &:active {
        background: var(--colored-background);
        color: white;
      }
    }
  }

  &--colored {
    color: white;
    background: var(--colored-background);

    &:disabled {
      color: rgba(45, 45, 58, 0.30);
      background: #B2B2BC;
    }

    @media (hover: hover) {
      &:hover {
        background: var(--dark-background);
        color: white;
      }
    }

    &:active {
      background: var(--colored-background);
      color: white;
    }

    @media (hover: none) {
      &:active {
        background: var(--dark-background);
        color: white;
      }
    }
  }

  &--border {
    border: 1px solid #c4c4c4;

    &:disabled {
      color: rgba(45, 45, 58, 0.30);
      background: #B2B2BC;
    }
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  &__badge {
    position: absolute;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: transform 0.1s linear;

    left: 50%;
    top: 50%;
    transform: translate(calc(-50% + 14px), calc(-50% - 12px));

    width: fit-content;
    min-width: 12px;
    height: 12px;

    padding: 0 2px;
    border-radius: 10px;

    flex-shrink: 0;
    font-size: 7px;
    line-height: 10px;
    font-weight: 700;

    color: white;
    background: #F95640;
  }

  &--with-text {
    .button__badge {
      display: none;
    }
  }

  &__icon--fixed-size {
    svg {
      width: 100%;
      height: 100%;
    }
  }
}

</style>